import {
    CompassOutlined,
    PieChartFilled,
    ShoppingCartOutlined,
    ShoppingOutlined,
    TeamOutlined,
} from "@ant-design/icons";
import { Badge } from "antd";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { UserRole } from "tm-shared/types";

import { ROUTES } from "@/constants";
import { useAppSelector } from "@/hooks/redux";

import { StyledFooter, StyledNavLink } from "./Styled";

interface NavItem {
    to: string;
    label: string;
    icon: JSX.Element;
    realActivePath?: string;
}

export const Footer = () => {
    const location = useLocation();
    const { checkoutList } = useAppSelector((state) => state.work);
    const { role } = useAppSelector((state) => state.user);
    const { activeSchedule } = useAppSelector((state) => state.sv);

    const commonLinks: Record<string, NavItem> = useMemo(
        () => ({
            [ROUTES.WorkPlan]: {
                icon: <ShoppingOutlined />,
                to: ROUTES.WorkPlan,
                label: "План Робіт",
            },
            [ROUTES.Dashboard]: {
                icon: <PieChartFilled />,
                to: ROUTES.Dashboard,
                label: "Дашборд",
            },
            [ROUTES.RouteManagement]: {
                icon: <CompassOutlined />,
                to: ROUTES.RouteManagement,
                label: "Маршрути",
            },
            [ROUTES.Team]: {
                icon: <TeamOutlined />,
                to: ROUTES.Team,
                label: "Команда",
            },
            [ROUTES.RouteManagementMyRoute]: {
                icon: <CompassOutlined />,
                to: ROUTES.RouteManagementMyRoute,
                label: "Мій маршрут",
            },
        }),
        [],
    );

    const roleLinks: Record<string, NavItem[]> = useMemo(
        () => ({
            [UserRole.Ta]: [
                commonLinks[ROUTES.Dashboard],
                {
                    icon: (
                        <Badge
                            count={Object.values(checkoutList)?.length || 0}
                            offset={[6, 17]}
                            status="success"
                            color="green"
                        >
                            <ShoppingCartOutlined />
                        </Badge>
                    ),
                    to: ROUTES.Checkout,
                    label: "Кошик",
                },
                commonLinks[ROUTES.WorkPlan],
            ],
            [UserRole.Md]: [
                commonLinks[ROUTES.Dashboard],
                commonLinks[ROUTES.WorkPlan],
            ],
            [UserRole.Sv]: [
                commonLinks[ROUTES.Dashboard],
                commonLinks[ROUTES.RouteManagement],
                commonLinks[ROUTES.Team],
                {
                    icon: <ShoppingCartOutlined />,
                    to: activeSchedule
                        ? `${ROUTES.SVCheckout}/${activeSchedule}`
                        : ROUTES.SVCheckout,
                    label: "Кошик",
                    realActivePath: ROUTES.SVCheckout,
                },
                commonLinks[ROUTES.WorkPlan],
            ],
            [UserRole.Svm]: [
                commonLinks[ROUTES.Dashboard],
                commonLinks[ROUTES.RouteManagement],
                commonLinks[ROUTES.Team],
                commonLinks[ROUTES.WorkPlan],
            ],
            [UserRole.Hr]: [
                {
                    icon: <TeamOutlined />,
                    to: ROUTES.HRTeam,
                    label: "Команда",
                },
            ],
            [UserRole.Kam]: [
                commonLinks[ROUTES.Dashboard],
                commonLinks[ROUTES.RouteManagementMyRoute],
                commonLinks[ROUTES.WorkPlan],
            ],
        }),
        [checkoutList, activeSchedule, commonLinks],
    );

    const renderNavLinks = roleLinks[role] || [];

    const renderLink = (link: NavItem) => {
        const isActive = link.realActivePath
            ? location.pathname.includes(link.realActivePath)
            : undefined;

        return (
            <StyledNavLink key={link.to} to={link.to} $isActive={isActive}>
                {link.icon}
                <span>{link.label}</span>
            </StyledNavLink>
        );
    };

    return <StyledFooter>{renderNavLinks.map(renderLink)}</StyledFooter>;
};
